import React, { useState, useEffect, useMemo } from 'react';
import { Search, Users, CheckCircle2, AlertCircle, Clock, ArrowLeft } from 'lucide-react';
import inAppMessageService from '../../services/inAppMessage.service';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getThemeColors } from '../../config/theme';

export const AudienceEditor = ({ selectedEmployeeIds, onSelectionChange, onPreview, onBack }) => {
  const { isDarkMode, t } = usePreferences();
  const colors = getThemeColors(isDarkMode);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyActiveAttendance, setOnlyActiveAttendance] = useState(false);

  useEffect(() => {
    fetchAudience();
  }, [onlyActiveAttendance]);

  const fetchAudience = async () => {
    setLoading(true);
    try {
      const response = await inAppMessageService.getAudience(onlyActiveAttendance);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching audience:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!searchQuery) return employees;
    const query = searchQuery.toLowerCase();
    return employees.filter(emp => 
      emp.name?.toLowerCase().includes(query) ||
      emp.position?.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  const handleToggleEmployee = (employeeId) => {
    const newSelection = selectedEmployeeIds.includes(employeeId)
      ? selectedEmployeeIds.filter(id => id !== employeeId)
      : [...selectedEmployeeIds, employeeId];
    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedEmployeeIds.length === filteredEmployees.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(filteredEmployees.map(emp => emp.id));
    }
  };

  const selectedCount = selectedEmployeeIds.length;
  const employeesWithToken = filteredEmployees.filter(emp => emp.has_fcm_token).length;
  const selectedWithToken = filteredEmployees
    .filter(emp => selectedEmployeeIds.includes(emp.id) && emp.has_fcm_token).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div style={{ color: colors.textSecondary }}>Loading employees...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-4 mb-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg transition-colors"
              style={{ color: colors.textSecondary }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.backgroundSecondary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.audience.title')}</h2>
        </div>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={18} style={{ color: colors.textTertiary }} />
          <input
            type="text"
            placeholder={t('pages.inAppMessages.audience.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 outline-none"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
            onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
          />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="activeAttendanceFilter"
            checked={onlyActiveAttendance}
            onChange={(e) => setOnlyActiveAttendance(e.target.checked)}
            className="w-4 h-4 rounded"
            style={{ accentColor: colors.primary }}
          />
          <label htmlFor="activeAttendanceFilter" className="text-sm cursor-pointer" style={{ color: colors.textPrimary }}>
            {t('pages.inAppMessages.audience.filterActiveAttendance')}
          </label>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span style={{ color: colors.textSecondary }}>
              {filteredEmployees.length} {filteredEmployees.length !== 1 ? t('pages.inAppMessages.audience.employees') : t('pages.inAppMessages.audience.employee')}
            </span>
            <span style={{ color: colors.textSecondary }}>
              {employeesWithToken} {t('pages.inAppMessages.audience.withFcmToken')}
            </span>
          </div>
          <button
            onClick={handleSelectAll}
            className="font-medium"
            style={{ color: colors.primary }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {selectedCount === filteredEmployees.length ? t('pages.inAppMessages.audience.deselectAll') : t('pages.inAppMessages.audience.selectAll')}
          </button>
        </div>
      </div>

      {/* Employee List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredEmployees.length === 0 ? (
          <div className="text-center py-8" style={{ color: colors.textSecondary }}>
            {t('pages.inAppMessages.audience.noEmployeesFound')}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredEmployees.map((employee) => {
              const isSelected = selectedEmployeeIds.includes(employee.id);
              const hasToken = employee.has_fcm_token === 1;
              const hasActiveAttendance = employee.has_active_attendance === 1;

              return (
                <div
                  key={employee.id}
                  onClick={() => handleToggleEmployee(employee.id)}
                  className="p-4 border-2 rounded-lg cursor-pointer transition-all"
                  style={{
                    borderColor: isSelected ? colors.primary : colors.border,
                    backgroundColor: isSelected ? (isDarkMode ? 'rgba(37, 99, 235, 0.1)' : 'rgba(99, 102, 241, 0.05)') : colors.background
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = colors.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = colors.border;
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleEmployee(employee.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-4 h-4 rounded"
                      style={{ accentColor: colors.primary }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold" style={{ color: colors.textPrimary }}>{employee.name}</span>
                        {hasActiveAttendance && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)', color: colors.success }}>
                            <Clock size={12} />
                            {t('pages.inAppMessages.audience.active')}
                          </span>
                        )}
                        {!hasToken && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)', color: colors.warning }}>
                            <AlertCircle size={12} />
                            {t('pages.inAppMessages.audience.noFcmToken')}
                          </span>
                        )}
                      </div>
                      {employee.position && (
                        <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{employee.position}</p>
                      )}
                      {employee.workplace_name && (
                        <p className="text-xs" style={{ color: colors.textTertiary }}>📍 {employee.workplace_name}</p>
                      )}
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="flex-shrink-0" size={20} style={{ color: colors.primary }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
        <div className="flex items-center justify-between mb-4">
            <div className="text-sm" style={{ color: colors.textSecondary }}>
              <span className="font-medium">{selectedCount}</span> {t('pages.inAppMessages.audience.selected')}
              {selectedCount > 0 && (
                <span style={{ color: colors.textTertiary }}>
                  {' '}({selectedWithToken} {t('pages.inAppMessages.audience.withFcmToken')})
                </span>
              )}
            </div>
            </div>
            <button
              onClick={onPreview}
              disabled={selectedCount === 0}
              className="w-full text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = colors.primaryHover;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary;
              }}
            >
              <Users size={18} />
              {t('pages.inAppMessages.audience.previewAndSend')}
            </button>
      </div>
    </div>
  );
};

